/**
 * MenuLayoutGallery — ekran wejścia edytora menu: przegląd zapisanych layoutów menu
 * jako lista albo kafelki, z przełącznikiem ikonkowym (lista / kafelki).
 *
 * Klik w layout → wejście do edytora (MenuBuilderShell przełącza widok na 'editor').
 * Wspólny dla scope admin i tenant (sterowany i18nPrefix). Tryb wyświetlania
 * (lista/kafelki) zapamiętany w localStorage per scope.
 *
 * Kreator menu tenanta (2026-07-02, makieta v2): opcjonalne rozszerzenia —
 *  - akcje Duplikuj/Usuń per layout (oba scope'y; renderują się gdy podano handlery),
 *  - sekcja „Menu modułów" (tylko tenant): gotowe menu z wykupionych modułów,
 *    read-only, z Podglądem i Duplikuj (kopia → edytowalny własny układ),
 *  - sekcja „Kartoteki do użycia w menu" (tylko tenant): pigułki z chipami modułów.
 * Admin nie podaje tych propsów — widok pozostaje identyczny jak dotąd, plus
 * akcje Duplikuj/Usuń (parytet z tenantem).
 */

import { useTranslation } from 'react-i18next';
import type { MenuLayoutSummary, MenuSourceKartoteka } from '@saas/shared';
import { ArrowLeft, Copy, Eye, LayoutGrid, List, Plus, Star, Trash2 } from 'lucide-react';

import { resolveIcon } from '@/lib/icon-map';

import { KartotekiSection } from './KartotekiSection';

export type GalleryView = 'tiles' | 'list';

/** Menu modułowe w galerii tenanta (read-only) — metadane do wiersza. */
export interface GalleryModuleMenu {
  code: string;
  name: string;
  icon: string | null;
  /** Liczba pozycji-liści menu (licznik w wierszu). */
  positions: number;
}

export interface MenuLayoutGalleryProps {
  layouts: MenuLayoutSummary[];
  isLoading: boolean;
  view: GalleryView;
  onViewChange: (view: GalleryView) => void;
  onOpen: (id: string) => void;
  onNew: () => void;
  onBack: () => void;
  i18nPrefix: string;
  /** Duplikuj własny layout (otwiera modal nazwy z klonem). Brak = bez przycisku. */
  onDuplicate?: (id: string) => void;
  /** Usuń własny layout (confirm w shellu). Brak = bez przycisku. */
  onDelete?: (id: string) => void;
  /** Tenant: menu modułów (read-only) nad sekcją własnych układów. */
  moduleMenus?: GalleryModuleMenu[];
  onModuleMenuPreview?: (code: string) => void;
  onModuleMenuDuplicate?: (code: string) => void;
  /** Tenant: kartoteki z chipami modułów — sekcja informacyjna pod listą. */
  kartoteki?: MenuSourceKartoteka[];
  /** Klasa wysokości kontenera — patrz `MenuBuilderShellProps.heightClassName`. */
  heightClassName?: string;
}

export function MenuLayoutGallery({
  layouts,
  isLoading,
  view,
  onViewChange,
  onOpen,
  onNew,
  onBack,
  i18nPrefix,
  onDuplicate,
  onDelete,
  moduleMenus,
  onModuleMenuPreview,
  onModuleMenuDuplicate,
  kartoteki,
  heightClassName = 'h-full',
}: MenuLayoutGalleryProps) {
  const { t, i18n } = useTranslation();

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language);
    } catch {
      return '';
    }
  };

  const hasModuleMenus = (moduleMenus?.length ?? 0) > 0;
  const hasKartoteki = (kartoteki?.length ?? 0) > 0;
  // Puste układy (hierarchy=[], np. seedowany „Default") nie udają menu — galeria
  // ich nie listuje; zostaje empty-state z przyciskiem tworzenia. Starsze mocki
  // bez pola isEmpty renderują się po staremu.
  const visibleLayouts = layouts.filter((l) => l.isEmpty !== true);

  const actionBtnClass =
    'flex items-center gap-1 rounded-md border border-border-secondary px-2 py-1 text-xs text-text-secondary transition hover:border-accent-300 hover:text-text-primary';

  /** Akcje Duplikuj/Usuń przy własnym layoucie (lista i kafelki). */
  const layoutActions = (l: MenuLayoutSummary) =>
    onDuplicate || onDelete ? (
      <span className="flex shrink-0 items-center gap-1.5">
        {onDuplicate ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(l.id);
            }}
            className={actionBtnClass}
            data-testid={`menu-layout-duplicate-${l.id}`}
          >
            <Copy className="h-3.5 w-3.5" />
            {t(`${i18nPrefix}.layouts.actions.duplicate`)}
          </button>
        ) : null}
        {onDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(l.id);
            }}
            aria-label={t(`${i18nPrefix}.layouts.actions.delete`)}
            title={t(`${i18nPrefix}.layouts.actions.delete`)}
            className="flex items-center rounded-md border border-border-secondary px-2 py-1 text-xs text-red-600 transition hover:border-red-300 dark:text-red-400"
            data-testid={`menu-layout-delete-${l.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </span>
    ) : null;

  return (
    <div
      className={`flex ${heightClassName} flex-col overflow-hidden rounded-2xl border border-border-secondary bg-bg-secondary`}
      data-testid="menu-builder-gallery"
    >
      {/* Toolbar */}
      <header className="flex items-center justify-between border-b border-border-secondary bg-bg-elevated px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
            data-testid="menu-gallery-back"
          >
            <ArrowLeft className="h-4 w-4" />
            {t(`${i18nPrefix}.back`)}
          </button>
          <h1 className="text-lg font-semibold text-text-primary">{t(`${i18nPrefix}.title`)}</h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Przełącznik lista / kafelki */}
          <div
            className="flex items-center rounded-md border border-border-secondary p-0.5"
            role="group"
            aria-label={t(`${i18nPrefix}.gallery.viewToggleLabel`)}
          >
            <button
              type="button"
              onClick={() => onViewChange('list')}
              aria-pressed={view === 'list'}
              aria-label={t(`${i18nPrefix}.gallery.viewList`)}
              title={t(`${i18nPrefix}.gallery.viewList`)}
              className={`rounded p-1.5 transition ${
                view === 'list'
                  ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                  : 'text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary'
              }`}
              data-testid="menu-gallery-view-list"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onViewChange('tiles')}
              aria-pressed={view === 'tiles'}
              aria-label={t(`${i18nPrefix}.gallery.viewTiles`)}
              title={t(`${i18nPrefix}.gallery.viewTiles`)}
              className={`rounded p-1.5 transition ${
                view === 'tiles'
                  ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                  : 'text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary'
              }`}
              data-testid="menu-gallery-view-tiles"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={onNew}
            className="flex items-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700"
            data-testid="menu-gallery-new"
          >
            <Plus className="h-4 w-4" />
            {t(`${i18nPrefix}.layouts.actions.new`)}
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="w-full max-w-5xl">
          {isLoading ? (
            <p className="text-sm text-text-muted">{t(`${i18nPrefix}.palette.loading`)}</p>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Sekcja menu modułów (tylko tenant) — read-only, Podgląd + Duplikuj */}
              {hasModuleMenus ? (
                <section data-testid="menu-gallery-module-menus">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {t(`${i18nPrefix}.gallery.moduleMenusHeader`, {
                      defaultValue: 'Menu modułów — tylko odczyt',
                    })}
                  </h2>
                  <p className="mt-0.5 text-xs text-text-secondary">
                    {t(`${i18nPrefix}.gallery.moduleMenusHint`, {
                      defaultValue:
                        'Gotowe menu z wykupionych modułów. Zduplikuj, żeby dostosować.',
                    })}
                  </p>
                  <div className="mt-2 flex flex-col divide-y divide-border-secondary overflow-hidden rounded-lg border border-border-secondary bg-bg-elevated">
                    {(moduleMenus ?? []).map((m) => {
                      const Icon = resolveIcon(m.icon);
                      return (
                        <div
                          key={m.code}
                          className="flex items-center gap-3 px-4 py-2.5"
                          data-testid={`menu-gallery-module-${m.code}`}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0 text-text-muted" />
                          <span className="truncate text-sm font-medium text-text-primary">
                            {m.name}
                          </span>
                          <span className="text-xs text-text-muted">
                            {t(`${i18nPrefix}.gallery.positionsCount`, {
                              count: m.positions,
                              defaultValue: 'pozycje: {{count}}',
                            })}
                          </span>
                          <span className="flex-1" />
                          {onModuleMenuPreview ? (
                            <button
                              type="button"
                              onClick={() => onModuleMenuPreview(m.code)}
                              className={actionBtnClass}
                              data-testid={`menu-gallery-module-preview-${m.code}`}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              {t(`${i18nPrefix}.gallery.previewModuleMenu`, {
                                defaultValue: 'Podgląd',
                              })}
                            </button>
                          ) : null}
                          {onModuleMenuDuplicate ? (
                            <button
                              type="button"
                              onClick={() => onModuleMenuDuplicate(m.code)}
                              className={actionBtnClass}
                              data-testid={`menu-gallery-module-duplicate-${m.code}`}
                            >
                              <Copy className="h-3.5 w-3.5" />
                              {t(`${i18nPrefix}.layouts.actions.duplicate`)}
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              {/* Własne layouty */}
              <section>
                {hasModuleMenus ? (
                  <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {t(`${i18nPrefix}.gallery.myMenusHeader`, { defaultValue: 'Moje menu' })}
                  </h2>
                ) : null}

                {visibleLayouts.length === 0 ? (
                  <div
                    className="mx-auto flex max-w-md flex-col items-center gap-3 py-10 text-center"
                    data-testid="menu-gallery-empty"
                  >
                    <h2 className="text-lg font-semibold text-text-primary">
                      {t(`${i18nPrefix}.layouts.empty.title`)}
                    </h2>
                    <p className="text-sm text-text-secondary">
                      {t(`${i18nPrefix}.layouts.empty.body`)}
                    </p>
                    <button
                      type="button"
                      onClick={onNew}
                      className="flex items-center gap-2 rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
                      data-testid="menu-gallery-empty-new"
                    >
                      <Plus className="h-4 w-4" />
                      {t(`${i18nPrefix}.layouts.actions.new`)}
                    </button>
                  </div>
                ) : view === 'tiles' ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {visibleLayouts.map((l) => (
                      <div
                        key={l.id}
                        className="flex flex-col gap-2 rounded-lg border border-border-secondary bg-bg-elevated p-4 transition hover:border-accent-300 hover:shadow-sm"
                      >
                        <button
                          type="button"
                          onClick={() => onOpen(l.id)}
                          className="flex flex-col gap-2 text-left"
                          data-testid={`menu-layout-open-${l.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="truncate font-medium text-text-primary">{l.name}</span>
                            {l.isDefault ? (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                <Star className="h-3 w-3" />
                                {t(`${i18nPrefix}.layouts.isDefaultBadge`)}
                              </span>
                            ) : null}
                          </div>
                          <span className="text-xs text-text-muted">
                            {t(`${i18nPrefix}.gallery.updatedAt`, { date: fmtDate(l.updatedAt) })}
                          </span>
                        </button>
                        {layoutActions(l)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col divide-y divide-border-secondary overflow-hidden rounded-lg border border-border-secondary bg-bg-elevated">
                    {visibleLayouts.map((l) => (
                      <div
                        key={l.id}
                        className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-bg-secondary"
                      >
                        <button
                          type="button"
                          onClick={() => onOpen(l.id)}
                          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                          data-testid={`menu-layout-open-${l.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="font-medium text-text-primary">{l.name}</span>
                            {l.isDefault ? (
                              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                <Star className="h-3 w-3" />
                                {t(`${i18nPrefix}.layouts.isDefaultBadge`)}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-xs text-text-muted">
                            {t(`${i18nPrefix}.gallery.updatedAt`, { date: fmtDate(l.updatedAt) })}
                          </span>
                        </button>
                        {layoutActions(l)}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Kartoteki z chipami modułów (tylko tenant) — wspólny KartotekiSection
                  (ta sama sekcja żyje w szynie kontekstu obok edytora). */}
              {hasKartoteki ? (
                <KartotekiSection kartoteki={kartoteki ?? []} i18nPrefix={i18nPrefix} />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
